import React from "react";
// Generate the ellipse paths
  // Not needed anymore as we're using SVG ellipse elements directlyimport React from "react";

  export default function ResearchAutomationChart(): JSX.Element {
    // Set dimensions and margins
    const width = 1000;
    const height = 700;
    const margin = { top: 60, right: 30, bottom: 60, left: 80 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
  
    // Define the log scale domains
    const xMin = 8;
    const xMax = 10000;
    const yMin = 10000;
    const yMax = 10000000;
  
    // Helper function to convert from linear to log scale position
    const logScaleX = (value: number): number => {
      const logXMin = Math.log10(xMin);
      const logXMax = Math.log10(xMax);
      const logValue = Math.log10(value);
      return margin.left + ((logValue - logXMin) / (logXMax - logXMin)) * innerWidth;
    };
  
    const logScaleY = (value: number): number => {
      const logYMin = Math.log10(yMin);
      const logYMax = Math.log10(yMax);
      const logValue = Math.log10(value);
      return margin.top + innerHeight - ((logValue - logYMin) / (logYMax - logYMin)) * innerHeight;
    };
  
    // X-axis ticks
    const xTicks = [10, 100, 1000, 10000];
    // Y-axis ticks
    const yTicks = [10000, 100000, 1000000, 10000000];

    const xMinorTicks = [20, 30, 40, 50, 60, 70, 80, 90, 200, 300, 400, 500, 600, 700, 800, 900, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000];
    // Add this to your yTicks array
    const yMinorTicks = [20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 2000000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 9000000];
    
    const MarColor = "rgb(11,78,16,.9)";
    const JunColor = "rgb(46,125,50,.85)";
    const SepColor = "rgb(102,187,106,.4)";

    // Define ellipse parameters for each month
    // Center coordinates and radii are in log scale units for direct SVG placement
    const rx = 570;
    const ry = 430;
    const h1 = 1.045;
    const h2 = h1*1.065;
    const v1 = 1.085;
    const v2 = v1*1.1;
    const ellipses = [
      { 
        month: "March 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx, 
        ry: ry, 
        color: MarColor
      },
      { 
        month: "June 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx*h1, 
        ry: ry*v1, 
        color: JunColor
      },
      { 
        month: "September 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx*h2, 
        ry: ry*v2, 
        color: SepColor
      }
    ];
  
    // Generate paths for SVG - generate ellipses cut off by axes
    const generateEllipsePath = (ellipse: {
      centerX: number,
      centerY: number,
      radiusX: number,
      radiusY: number
    }): string => {
      // Sample points around the ellipse to create the path
      const points: [number, number][] = [];
      const steps = 180; // Number of points to sample
      
      for (let i = 0; i <= steps; i++) {
        // Angle from 0 to PI (top half of ellipse)
        const angle = Math.PI * i / steps;
        
        // Calculate point on ellipse
        const x = ellipse.centerX - ellipse.radiusX * Math.cos(angle);
        const y = ellipse.centerY - ellipse.radiusY * Math.sin(angle);
        
        // Only include points within the visible range
        if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) {
          points.push([x, y]);
        }
      }
      
      // Create SVG path
      return points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${logScaleX(p[0])} ${logScaleY(p[1])}`)
        .join(" ");
    };
  
    // Define notable points on the curves
    const point1 = { x: 13*30, y: 200000, label: "200K copies\n30x Human\nspeed" };
    const point2 = { x: 13*50, y: 300000, label: "300K copies\n50x Human\nspeed" };
    const point3 = { x: 13*36.5, y: 240000, label: "" };
  
    // Define baseline human speeds for reference
    const humanSpeeds = [
      { x: 10, y: 10000, label: "Human thinking speed\n10 words/sec" },
      { x: 100, y: 10000, label: "10x Human\nthinking speed" },
      { x: 1000, y: 10000, label: "100x Human\nthinking speed" }
    ];
  
    // Define colors
    const colors = {
      march: MarColor, // Dark green
      june: JunColor,  // Medium green
      september: SepColor, // Light green
      grid: "#cccccc",  // Light gray
      text: "#333333",  // Dark gray for text
      point: "#000000"  // Black for points
    };
  
    return (
      <div className="w-full relative overflow-hidden mt-8 mb-8">
        <svg
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          viewBox={`0 0 ${width} ${height}`}
        >
          {/* Chart Title */}
          <text
            x={margin.left}
            y={margin.top / 1.5}
            fontSize="32"
            fontFamily="monospace"
            textAnchor="left"
            fill={colors.text}
            className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          >
            Research Automation Deployment Tradeoff
          </text>
  
          {/* Legend */}
          <g transform={`translate(${width - margin.right - 150}, ${margin.top + 20})`}>
            {/* March 2027 */}
            <line x1="0" y1="0" x2="30" y2="0" stroke={colors.march} strokeWidth="2" />
            <text x="40" y="5" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Mar 2027</text>
            
            {/* June 2027 */}
            <line x1="0" y1="25" x2="30" y2="25" stroke={colors.june} strokeWidth="2" />
            <text x="40" y="30" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Jun 2027</text>
            
            {/* September 2027 */}
            <line x1="0" y1="50" x2="30" y2="50" stroke={colors.september} strokeWidth="2" />
            <text x="40" y="55" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Sep 2027</text>
          </g>
  
          {/* Grid lines */}
          {/* Vertical grid lines */}
          {xTicks.map((tick) => (
            <line
              key={`vgrid-${tick}`}
              x1={logScaleX(tick)}
              y1={margin.top}
              x2={logScaleX(tick)}
              y2={margin.top + innerHeight}
              stroke={colors.grid}
              strokeWidth="1"
              opacity="0.7"
            />
          ))}
  
          {/* Horizontal grid lines */}
          {yTicks.map((tick) => (
            <line
              key={`hgrid-${tick}`}
              x1={margin.left}
              y1={logScaleY(tick)}
              x2={margin.left + innerWidth}
              y2={logScaleY(tick)}
              stroke={colors.grid}
              strokeWidth="1"
              opacity="0.7"
            />
          ))}
        {/* Minor X-axis grid lines */}
        {xMinorTicks.map((tick) => (
        <line
            key={`vgrid-minor-${tick}`}
            x1={logScaleX(tick)}
            y1={margin.top}
            x2={logScaleX(tick)}
            y2={margin.top + innerHeight}
            stroke={colors.grid}
            strokeWidth="0.5"
            opacity="0.5"
        />
        ))}

        {/* Minor Y-axis grid lines */}
        {yMinorTicks.map((tick) => (
        <line
            key={`hgrid-minor-${tick}`}
            x1={margin.left}
            y1={logScaleY(tick)}
            x2={margin.left + innerWidth}
            y2={logScaleY(tick)}
            stroke={colors.grid}
            strokeWidth="0.5"
            opacity="0.5"
        />
        ))}

        {/* Special dotted lines for x = 13, 130, and 1300 */}
        {[13.3, 133, 1330].map((tick) => (
        <line
            key={`special-${tick}`}
            x1={logScaleX(tick)}
            y1={margin.top}
            x2={logScaleX(tick)}
            y2={margin.top + innerHeight}
            stroke="#20aE20" // Light green color
            strokeWidth="1,5"
            strokeDasharray="4 4" // Creates dotted line
            opacity="0.8"
        />
        ))}
          {/* X-axis */}
          <line
            x1={margin.left}
            y1={margin.top + innerHeight}
            x2={margin.left + innerWidth}
            y2={margin.top + innerHeight}
            stroke={colors.text}
            strokeWidth="1.5"
          />
  
          {/* Y-axis */}
          <line
            x1={margin.left}
            y1={margin.top}
            x2={margin.left}
            y2={margin.top + innerHeight}
            stroke={colors.text}
            strokeWidth="1.5"
          />
  
          {/* X-axis label */}
          <text
            x={margin.left + innerWidth / 2}
            y={height-10}
            textAnchor="middle"
            fontSize="20"
            fontFamily="monospace"
            className="text-[20px] font-mono"
            fill={colors.text}
          >
            Speed (tokens/sec)
          </text>
  
          {/* Y-axis label */}
          <text
            transform={`rotate(-90, ${margin.left / 3}, ${margin.top + innerHeight / 2})`}
            x={margin.left / 3}
            y={margin.top + innerHeight / 2.09}
            textAnchor="middle"
            fontSize="20"
            fontFamily="monospace"
            className="text-[20px] font-mono"
            fill={colors.text}
          >
            Parallel Copies
          </text>
  
          {/* X-axis ticks */}
          {xTicks.map((tick) => (
            <g key={`xtick-${tick}`}>
              <line
                x1={logScaleX(tick)}
                y1={margin.top + innerHeight}
                x2={logScaleX(tick)}
                y2={margin.top + innerHeight + 5}
                stroke={colors.text}
                strokeWidth="1.5"
              />
              <text
                x={logScaleX(tick)}
                y={margin.top + innerHeight + 25}
                textAnchor="middle"
                fontSize="16"
                fontFamily="monospace"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {tick.toLocaleString()}
              </text>
            </g>
          ))}
  
          {/* Y-axis ticks */}
          {yTicks.map((tick) => (
            <g key={`ytick-${tick}`}>
              <line
                x1={margin.left - 5}
                y1={logScaleY(tick)}
                x2={margin.left}
                y2={logScaleY(tick)}
                stroke={colors.text}
                strokeWidth="1.5"
              />
              <text
                x={margin.left - 10}
                y={logScaleY(tick) + 5}
                textAnchor="end"
                fontSize="16"
                fontFamily="monospace"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {tick === 10000 ? "10K" : tick === 100000 ? "100K" : tick === 1000000 ? "1M" : "10M"}
              </text>
            </g>
          ))}
  
          {/* Draw the ellipses */}
          {ellipses.map((item, index) => (
            <React.Fragment key={`ellipse-${index}`}>
              {/* Create a clipping path to cut off the ellipse at the axes */}
              <defs>
                <clipPath id={`clip-${index}`}>
                  <rect x={margin.left} y={margin.top} width={innerWidth} height={innerHeight} />
                </clipPath>
              </defs>
              
              {/* Draw the ellipse with the clipping path */}
              <ellipse
                cx={item.cx}
                cy={item.cy}
                rx={item.rx}
                ry={item.ry}
                fill="none"
                stroke={item.color}
                strokeWidth="2.5"
                clipPath={`url(#clip-${index})`}
              />
            </React.Fragment>
          ))}
  
          {/* Draw notable points */}
          {/* Point 1 on March curve */}
          <circle
            cx={logScaleX(point1.x)}
            cy={logScaleY(point1.y)}
            r="6"
            fill={"rgb(11,78,16)"}
          />
          <rect
            x={logScaleX(point1.x) - 125}
            y={logScaleY(point1.y) + 5}
            width="120"
            height="60"
            rx="5"
            ry="5"
            fill="rgba(255, 255, 255, 0.8)"
            stroke="#ccc"
          />
          <text
            x={logScaleX(point1.x)}
            y={logScaleY(point1.y) + 23}
            textAnchor="middle"
            fontSize="16"
            fontFamily="monospace"
            className="text-[16px] font-mono"
            fill={colors.text}
          >
            <tspan x={500} dy="0">200K copies</tspan>
            <tspan x={500} dy="18">30x Human</tspan>
            <tspan x={500} dy="18">speed</tspan>
          </text>
  
          {/* Point 2 on June curve */}
          <circle
            cx={logScaleX(point2.x)}
            cy={logScaleY(point2.y)}
            r="6"
            fill={"rgb(155,211,158)"}
          />
          <rect
            x={logScaleX(point2.x) + 5}
            y={logScaleY(point2.y) - 65}
            width="120"
            height="60"
            rx="5"
            ry="5"
            fill="rgba(255, 255, 255, 0.8)"
            stroke="#ccc"
          />
          <text
            x={598}
            y={logScaleY(point2.y) - 46}
            textAnchor="middle"
            fontSize="16"
            fontFamily="monospace"
            className="text-[16px] font-mono"
            fill={colors.text}
          >
            <tspan x={694} dy="0">300K copies</tspan>
            <tspan x={694} dy="18">50x Human</tspan>
            <tspan x={694} dy="18">speed</tspan>
          </text>

          <circle
            cx={logScaleX(point3.x)}
            cy={logScaleY(point3.y)}
            r="6"
            fill={"rgb(46,125,50)"}
          />
      {/* const MarColor = "rgb(11,78,16,.9)";
    const JunColor = "rgb(46,125,50,.85)";
    const SepColor = "rgb(102,187,106,.4)"; */}
          {/* Human speed reference labels */}
          {humanSpeeds.map((speed, i) => (
            <g key={`speed-${i}`}>
              <text
                x={logScaleX(speed.x)}
                y={logScaleY(speed.y) - 20}
                textAnchor="left"
                fontSize="16"
                fontFamily="monospace"
                className="text-[16px] font-mono"
                fill={MarColor}
              >
                {speed.label.split('\n').map((line, j) => (
                  <tspan key={j} x={logScaleX(speed.x)+48} dy={j > 0 ? 15 : 0}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  }